'use client'

import React from 'react'
import TimeSlot from './TimeSlot'
import { DateTime } from 'luxon'

/**
 * TimeSlotPicker Component
 * Displays available time slots for a selected date
 *
 * @param {Object} props - Component props
 * @param {string} props.selectedDate - Currently selected date (YYYY-MM-DD)
 * @param {Array} props.slots - Array of slot objects [{id, start, end}]
 * @param {Function} props.onSlotSelect - Callback when slot is selected (receives slot object)
 * @param {number} props.selectedSlotId - Currently selected slot ID
 * @param {string} props.className - Additional CSS classes
 *
 * @example
 * <TimeSlotPicker
 *   selectedDate="2025-12-05"
 *   slots={[{id: 202, start: "08:30", end: "09:15"}]}
 *   onSlotSelect={(slot) => console.log(slot)}
 *   selectedSlotId={202}
 * />
 */
const TimeSlotPicker = ({ selectedDate, slots, onSlotSelect, selectedSlotId, className = '' }) => {


  // If no date selected
  if (!selectedDate) {
    return (
      <div className={`p-6 text-center text-gray-500 ${className}`}>
        <p className="text-sm">Please select a date to view available time slots</p>
      </div>
    )
  }

  // If no slots available for this date
  if (!slots || slots.length === 0) {
    return (
      <div className={`p-6 text-center ${className}`}>
        <p className="text-sm text-gray-600 mb-2">No time slots available for this date</p>
        <p className="text-xs text-gray-500">Please select another date</p>
      </div>
    )
  }

  // Format date for display
  const formattedDate = DateTime.fromISO(selectedDate).toFormat('MMMM d, yyyy')

  return (
    <div className={`${className}`}>
      {/* Header */}
      <div className="mb-4">
        <h4 className="text-base font-semibold text-primary mb-1">
          Available Time Slots
        </h4>
        <p className="text-sm text-gray-600">{formattedDate}</p>
        <p className="text-xs text-gray-500 mt-1">
          ⏰ All times shown in Pacific Time (PT)
        </p>
      </div>

      {/* Time slots grid */}
      <div className="grid grid-cols-2 gap-3 max-xs:grid-cols-1">
        {slots.map((slot) => (
          <TimeSlot
            key={slot.id}
            slot={slot}
            onSelect={onSlotSelect}
            isSelected={selectedSlotId === slot.id}
          />
        ))}
      </div>
    </div>
  )
}

export default TimeSlotPicker
